import Big from 'big.js';
import { ContractBenefit } from '../models';

export class MarginCalculatorInsuranceHelperService {
    static getPreviouslyEnrolledDental(previousContract: ContractBenefit): boolean {
        return MarginCalculatorInsuranceHelperService.getPreviouslyEnrolled(previousContract)
            ? previousContract?.dental
            : false;
    }

    static getPreviouslyEnrolledMedical(previousContract: ContractBenefit): boolean {
        return MarginCalculatorInsuranceHelperService.getPreviouslyEnrolled(previousContract)
            ? previousContract?.medical
            : false;
    }

    static getPreviouslyEnrolled(previousContract: ContractBenefit): boolean {
        return !!previousContract?.lastEnrollmentDate?.trim();
    }

    static getTotalDentalCost(
        newEffectiveDate: Date,
        oldEffectiveDate: Date,
        startDate: Date,
        endDate: Date,
        dentalDays: number,
        dentalCost: number,
        daysInGap: number,
        newVersionDentalCost: number,
        continuedInsurance: boolean,
        totalDays: number,
        daysInContract: number,
        daysInGapBeforeNewEffectiveDate: number,
        daysInGapOnOrAfterNewEffectiveDate: number,
        datePipelined: Date,
        releaseNewEffectiveDate: Date,
        needInsurance: boolean
    ): number {
        let totalDentalCost = 0;
        let effectiveDate: Date = newEffectiveDate;

        if (needInsurance) {
            if (
                MarginCalculatorInsuranceHelperService._isPipelinedBeforeReleaseNewEffectiveDate(
                    datePipelined,
                    releaseNewEffectiveDate
                )
            ) {
                effectiveDate = oldEffectiveDate;
            }

            if (startDate >= effectiveDate) {
                totalDentalCost = MarginCalculatorInsuranceHelperService.totalDentalCostStartDateAfterNewEffectiveDate(
                    totalDays,
                    daysInContract,
                    daysInGap,
                    dentalDays,
                    dentalCost,
                    newVersionDentalCost,
                    continuedInsurance,
                    daysInGapBeforeNewEffectiveDate,
                    daysInGapOnOrAfterNewEffectiveDate,
                    datePipelined,
                    releaseNewEffectiveDate
                );
            } else if (endDate < effectiveDate) {
                totalDentalCost = dentalDays && dentalCost ? Big(dentalDays).mul(dentalCost).toNumber() : 0;
            } else if (startDate < effectiveDate && endDate >= effectiveDate) {
                totalDentalCost = MarginCalculatorInsuranceHelperService.totalDentalCostStartDateBeforeNewEffectiveDate(
                    effectiveDate,
                    startDate,
                    endDate,
                    daysInGap,
                    dentalDays,
                    dentalCost,
                    newVersionDentalCost,
                    continuedInsurance
                );
            }
        }
        return totalDentalCost;
    }

    private static totalDentalCostStartDateBeforeNewEffectiveDate(
        effectiveDate: Date,
        startDate: Date,
        endDate: Date,
        daysInGap: number,
        dentalDays: number,
        dentalCost: number,
        newVersionDentalCost: number,
        continuedInsurance: boolean
    ) {
        let totalDentalCost = 0;
        let dentalCostForGapDays = 0;
        const dentalDaysForCurrentVersion = MarginCalculatorInsuranceHelperService._getDayDiff(startDate, effectiveDate);
        const dentalDaysForNewVersion = MarginCalculatorInsuranceHelperService._getDayDiff(effectiveDate, endDate) + 1;
        let costForExtraDentalDays = 0;
        let totalDentalDays = 0;
        let extraDays = 0;

        if (continuedInsurance && daysInGap && daysInGap > 0) {
            dentalCostForGapDays = daysInGap && dentalCost ? Big(daysInGap).mul(dentalCost).toNumber() : 0;
            totalDentalDays = dentalDaysForCurrentVersion + dentalDaysForNewVersion + daysInGap;
        } else {
            totalDentalDays = dentalDaysForCurrentVersion + dentalDaysForNewVersion;
        }

        if (totalDentalDays !== dentalDays) {
            extraDays = dentalDays - totalDentalDays;
            costForExtraDentalDays =
                newVersionDentalCost && extraDays ? Big(extraDays).mul(newVersionDentalCost).toNumber() : 0;
        }
        if (extraDays < 0) {
            totalDentalCost =
                dentalDays && newVersionDentalCost ? Big(dentalDays).mul(newVersionDentalCost).toNumber() : 0;
        } else {
            totalDentalCost =
                (dentalDaysForCurrentVersion && dentalCost
                    ? Big(dentalDaysForCurrentVersion).mul(dentalCost).toNumber()
                    : 0) +
                (dentalDaysForNewVersion && newVersionDentalCost
                    ? Big(dentalDaysForNewVersion).mul(newVersionDentalCost).toNumber()
                    : 0) +
                dentalCostForGapDays +
                costForExtraDentalDays;
        }
        return totalDentalCost;
    }

    private static totalDentalCostStartDateAfterNewEffectiveDate(
        totalDays: number,
        daysInContract: number,
        daysInGap: number,
        dentalDays: number,
        dentalCost: number,
        newVersionDentalCost: number,
        continuedInsurance: boolean,
        daysInGapBeforeNewEffectiveDate: number,
        daysInGapOnOrAfterNewEffectiveDate: number,
        datePipelined: Date,
        releaseNewEffectiveDate: Date
    ): number {
        let totalDentalCost = 0;
        let dentalCostForGapDays = 0;
        const cost = MarginCalculatorInsuranceHelperService._isPipelinedBeforeReleaseNewEffectiveDate(datePipelined, releaseNewEffectiveDate)
            ? dentalCost
            : newVersionDentalCost;

        if (continuedInsurance && daysInGap && daysInGap > 0) {
            let costForExtraDentalDays = 0;
            dentalCostForGapDays =
                (daysInGapBeforeNewEffectiveDate && dentalCost
                    ? Big(daysInGapBeforeNewEffectiveDate).mul(dentalCost).toNumber()
                    : 0) +
                (daysInGapOnOrAfterNewEffectiveDate && cost
                    ? Big(daysInGapOnOrAfterNewEffectiveDate).mul(cost).toNumber()
                    : 0);
            if (totalDays !== dentalDays) {
                const extraDays = dentalDays - totalDays;
                costForExtraDentalDays = extraDays && cost ? Big(extraDays).mul(cost).toNumber() : 0;
            }
            totalDentalCost =
                (daysInContract && cost ? Big(daysInContract).mul(cost).toNumber() : 0) +
                dentalCostForGapDays +
                costForExtraDentalDays;
        } else {
            totalDentalCost = dentalDays && cost ? Big(dentalDays).mul(cost).toNumber() : 0;
        }
        return totalDentalCost;
    }

    static getTotalMedicalCost(
        newEffectiveDate: Date,
        oldEffectiveDate: Date,
        startDate: Date,
        endDate: Date,
        medicalDays: number,
        medicalCost: number,
        daysInGap: number,
        newVersionMedicalCost: number,
        continuedInsurance: boolean,
        totalDays: number,
        daysInContract: number,
        daysInGapBeforeNewEffectiveDate: number,
        daysInGapOnOrAfterNewEffectiveDate: number,
        datePipelined: Date,
        releaseNewEffectiveDate: Date,
        needInsurance: boolean
    ): number {
        let effectiveDate: Date = newEffectiveDate;
        let totalMedicalCost = 0;

        if (needInsurance) {
            if (MarginCalculatorInsuranceHelperService._isPipelinedBeforeReleaseNewEffectiveDate(datePipelined, releaseNewEffectiveDate)) {
                effectiveDate = oldEffectiveDate;
            }

            if (startDate >= effectiveDate) {
                totalMedicalCost = MarginCalculatorInsuranceHelperService.totalMedicalCostStartDateAfterEffectiveDate(
                    totalDays,
                    daysInContract,
                    daysInGap,
                    medicalDays,
                    medicalCost,
                    newVersionMedicalCost,
                    continuedInsurance,
                    daysInGapBeforeNewEffectiveDate,
                    daysInGapOnOrAfterNewEffectiveDate,
                    datePipelined,
                    releaseNewEffectiveDate
                );
            } else if (endDate < effectiveDate) {
                totalMedicalCost = medicalDays && medicalCost ? Big(medicalDays).mul(medicalCost).toNumber() : 0;
            } else if (startDate < effectiveDate && endDate >= effectiveDate) {
                totalMedicalCost = MarginCalculatorInsuranceHelperService.totalMedicalCostStartDateBeforeEffectiveDate(
                    effectiveDate,
                    startDate,
                    endDate,
                    daysInGap,
                    medicalDays,
                    medicalCost,
                    newVersionMedicalCost,
                    continuedInsurance
                );
            }
        }
        return totalMedicalCost;
    }

    private static totalMedicalCostStartDateBeforeEffectiveDate(
        effectiveDate: Date,
        startDate: Date,
        endDate: Date,
        daysInGap: number,
        medicalDays: number,
        medicalCost: number,
        newVersionMedicalCost: number,
        continuedInsurance: boolean
    ): number {
        let medicalCostForGapDays = 0;
        const medicalDaysForCurrentVersion = MarginCalculatorInsuranceHelperService._getDayDiff(startDate, effectiveDate);
        const medicalDaysForNewVersion = MarginCalculatorInsuranceHelperService._getDayDiff(effectiveDate, endDate) + 1;
        let costForExtraMedicalDays = 0;
        let totalMedicalDays = 0;
        let extraDays = 0;
        let totalMedicalCost = 0;

        if (continuedInsurance && daysInGap && daysInGap > 0) {
            medicalCostForGapDays = medicalCost ? Big(daysInGap).mul(medicalCost).toNumber() : 0;
            totalMedicalDays = medicalDaysForCurrentVersion + medicalDaysForNewVersion + daysInGap;
        } else {
            totalMedicalDays = medicalDaysForCurrentVersion + medicalDaysForNewVersion;
        }

        if (totalMedicalDays !== medicalDays) {
            extraDays = medicalDays - totalMedicalDays;
            costForExtraMedicalDays =
                newVersionMedicalCost && extraDays ? Big(extraDays).mul(newVersionMedicalCost).toNumber() : 0;
        }
        if (extraDays < 0) {
            totalMedicalCost =
                newVersionMedicalCost && medicalDays ? Big(medicalDays).mul(newVersionMedicalCost).toNumber() : 0;
        } else {
            totalMedicalCost =
                (medicalDaysForCurrentVersion && medicalCost
                    ? Big(medicalDaysForCurrentVersion).mul(medicalCost).toNumber()
                    : 0) +
                (medicalDaysForNewVersion && newVersionMedicalCost
                    ? Big(medicalDaysForNewVersion).mul(newVersionMedicalCost).toNumber()
                    : 0) +
                medicalCostForGapDays +
                costForExtraMedicalDays;
        }
        return totalMedicalCost;
    }

    private static totalMedicalCostStartDateAfterEffectiveDate(
        totalDays: number,
        daysInContract: number,
        daysInGap: number,
        medicalDays: number,
        medicalCost: number,
        newVersionMedicalCost: number,
        continuedInsurance: boolean,
        daysInGapBeforeNewEffectiveDate: number,
        daysInGapOnOrAfterNewEffectiveDate: number,
        datePipelined: Date,
        releaseNewEffectiveDate: Date
    ): number {
        let medicalCostForGapDays = 0;
        let totalMedicalCost = 0;
        const cost = MarginCalculatorInsuranceHelperService._isPipelinedBeforeReleaseNewEffectiveDate(datePipelined, releaseNewEffectiveDate)
            ? medicalCost
            : newVersionMedicalCost;

        if (continuedInsurance && daysInGap && daysInGap > 0) {
            let costForExtraMedicalDays = 0;
            medicalCostForGapDays =
                (daysInGapBeforeNewEffectiveDate && medicalCost
                    ? Big(daysInGapBeforeNewEffectiveDate).mul(medicalCost).toNumber()
                    : 0) +
                (daysInGapOnOrAfterNewEffectiveDate && cost
                    ? Big(daysInGapOnOrAfterNewEffectiveDate).mul(cost).toNumber()
                    : 0);
            if (totalDays !== medicalDays) {
                const extraDays = medicalDays - totalDays;
                costForExtraMedicalDays = extraDays && cost ? Big(extraDays).mul(cost).toNumber() : 0;
            }
            totalMedicalCost =
                (daysInContract && cost ? Big(daysInContract).mul(cost).toNumber() : 0) +
                medicalCostForGapDays +
                costForExtraMedicalDays;
        } else {
            totalMedicalCost = medicalDays && cost ? Big(medicalDays).mul(cost).toNumber() : 0;
        }
        return totalMedicalCost;
    }

    private static _getDayDiff(startDate: Date, endDate: Date): number {
        const msInDay = 24 * 60 * 60 * 1000;
        return Math.round(Math.abs(Number(endDate) - Number(startDate)) / msInDay);
    }

    private static _isPipelinedBeforeReleaseNewEffectiveDate(
        datePipelined: Date,
        releaseNewEffectiveDate: Date
    ): boolean {
        return datePipelined && new Date(datePipelined) < releaseNewEffectiveDate;
    }
}
